import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { User, LogOut, Menu, X } from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';

const Navbar = ({ onLoginClick }) => {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const [mobileOpen, setMobileOpen] = useState(false);

  const navLinks = [
    { label: 'About', path: '/about', testId: 'nav-about-link' },
    { label: 'Services', path: '/services', testId: 'nav-services-link' },
    { label: 'Credit Cards', path: '/credit-cards', testId: 'nav-credit-cards-link' },
    { label: 'Tax Services', path: '/tax-services', testId: 'nav-tax-services-link', highlight: true },
    { label: 'Compare', path: '/compare', testId: 'nav-compare-link' },
    { label: 'Calculators', path: '/calculators', testId: 'nav-calculators-link' },
    { label: 'Credit Score', path: '/credit-score', testId: 'nav-credit-score-link' },
    { label: 'Quick Enquiry', path: '/enquiry', testId: 'nav-enquiry-link' },
    { label: 'Utilities', path: '/utilities', testId: 'nav-utilities-link' },
    { label: 'Contact', path: '/contact', testId: 'nav-contact-link' },
  ];

  return (
    <nav className="nav-header" style={{ padding: '0.4rem 0' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem' }}>
        <div 
          style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', flexShrink: 0 }} 
          onClick={() => navigate('/')}
          data-testid="logo-home-link"
        >
          <img src="/arthik-logo.svg" alt="Arthik" style={{ height: 36, display: 'block' }} />
        </div>

        {/* Desktop Menu */}
        <div className="desktop-nav" style={{ display: 'flex', gap: '0.55rem', alignItems: 'center' }}>
          {!user && navLinks.map(link => (
            <button
              key={link.path}
              onClick={() => navigate(link.path)}
              style={link.highlight
                ? { background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', color: 'white', border: 'none', borderRadius: '0.45rem', padding: '0.35rem 0.7rem', fontWeight: '700', cursor: 'pointer', fontSize: '0.78rem', whiteSpace: 'nowrap', boxShadow: '0 2px 6px rgba(16,185,129,0.3)' }
                : { background: 'none', border: 'none', color: '#475569', fontWeight: '600', cursor: 'pointer', fontSize: '0.78rem', whiteSpace: 'nowrap', padding: '0.25rem 0.15rem' }}
              data-testid={link.testId}
            >
              {link.label}
            </button>
          ))}

          {user ? (
            <>
              <button 
                onClick={() => navigate('/dashboard')}
                style={{ background: 'none', border: 'none', color: '#475569', fontWeight: '600', cursor: 'pointer', fontSize: '0.85rem', whiteSpace: 'nowrap', padding: '0.3rem 0.4rem' }}
                data-testid="nav-dashboard-link"
              >
                Dashboard
              </button>
              <button 
                onClick={() => navigate('/enquiry')}
                style={{ background: 'none', border: 'none', color: '#475569', fontWeight: '600', cursor: 'pointer', fontSize: '0.85rem', whiteSpace: 'nowrap', padding: '0.3rem 0.4rem' }}
                data-testid="nav-enquiry-loggedin"
              >
                Enquiry
              </button>
              <button 
                onClick={() => navigate('/tax-services')}
                style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', color: 'white', border: 'none', borderRadius: '0.45rem', padding: '0.4rem 0.85rem', fontWeight: '700', cursor: 'pointer', fontSize: '0.8rem', whiteSpace: 'nowrap', boxShadow: '0 2px 6px rgba(16,185,129,0.3)' }}
                data-testid="nav-tax-services-loggedin"
              >
                Tax
              </button>
              <button 
                onClick={() => navigate('/referrals')}
                style={{ background: 'none', border: 'none', color: '#FF6B35', fontWeight: '700', cursor: 'pointer', fontSize: '0.85rem', whiteSpace: 'nowrap', padding: '0.3rem 0.4rem' }}
                data-testid="nav-referrals-link"
              >
                Refer & Earn
              </button>
              {user.role === 'admin' && (
                <button 
                  onClick={() => navigate('/admin')}
                  style={{ background: 'linear-gradient(135deg, #FF6B35 0%, #E55100 100%)', color: 'white', border: 'none', borderRadius: '0.45rem', padding: '0.45rem 0.95rem', fontWeight: '700', cursor: 'pointer', fontSize: '0.85rem', whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(255,107,53,0.3)' }}
                  data-testid="nav-admin-link"
                >
                  Admin
                </button>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.3rem 0.7rem', background: '#FFF7ED', borderRadius: 999 }}>
                <User size={14} style={{ color: '#FF6B35' }} />
                <span style={{ color: '#0F172A', fontWeight: '600', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>{user.full_name.split(' ')[0]}</span>
              </div>
              <button 
                onClick={logout}
                className="outline-button"
                style={{ padding: '0.45rem 0.95rem', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                data-testid="logout-button"
              >
                <LogOut size={14} />
                Logout
              </button>
            </>
          ) : (
            <button 
              onClick={() => { if (onLoginClick) onLoginClick(); else navigate('/?auth=login'); }}
              className="primary-button"
              style={{ padding: '0.45rem 1rem', fontSize: '0.78rem', whiteSpace: 'nowrap', marginLeft: '0.4rem' }}
              data-testid="login-button"
            >
              Login
            </button>
          )}
          <LanguageSwitcher />
        </div>

        {/* Mobile Menu Button */}
        <button
          className="mobile-menu-btn"
          onClick={() => setMobileOpen(!mobileOpen)}
          style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer' }}
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div style={{ padding: '1rem 2rem', borderTop: '1px solid #E2E8F0', backgroundColor: 'white' }}>
          {navLinks.map(link => (
            <button
              key={link.path}
              onClick={() => { navigate(link.path); setMobileOpen(false); }}
              style={{ display: 'block', width: '100%', padding: '0.75rem 0', background: 'none', border: 'none', textAlign: 'left', color: '#475569', fontWeight: '600', cursor: 'pointer' }}
            >
              {link.label}
            </button>
          ))}
          {user && (
            <button
              onClick={() => { navigate('/dashboard'); setMobileOpen(false); }}
              style={{ display: 'block', width: '100%', padding: '0.75rem 0', background: 'none', border: 'none', textAlign: 'left', color: '#475569', fontWeight: '600', cursor: 'pointer' }}
            >
              Dashboard
            </button>
          )}
        </div>
      )}

      <style>{`
        @media (max-width: 880px) {
          .desktop-nav { display: none !important; }
          .mobile-menu-btn { display: block !important; }
        }
      `}</style>
    </nav>
  );
};

export default Navbar;
